import { useState, useMemo, useRef } from 'react'
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Share2, 
  BarChart3, 
  BookOpen,
  Download,
  Share,
  Info,
  Play,
  X,
  Calendar,
  Clock,
  AlertCircle,
} from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { useLanguage } from '../contexts/LanguageContext'
import translations from '../locales/translations'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import FAQAccordion from '../components/FAQAccordion'

interface Category {
  id: string
  name: string
  icon: typeof TrendingUp
  description: string
}

interface Tool {
  id: string
  name: string
  formula: string
  inputs: Array<{ id: string; label: string; placeholder: string }>
  calculate: (values: Record<string, number>) => number
  unit?: string
  description: string
}

const MarketingTools = () => {
  const { language } = useLanguage()
  const [selectedCategory, setSelectedCategory] = useState<string>('roas-roi')
  const [selectedTool, setSelectedTool] = useState<string>('ecommerce-economics')
  const [inputValues, setInputValues] = useState<Record<string, number>>({})
  const [result, setResult] = useState<number | null>(null)
  const [vatRate, setVatRate] = useState<number>(10)
  
  const pageContent = useMemo(() => {
    return (translations as any)[language].marketingTools
  }, [language])
  
  const homeContent = useMemo(() => {
    return (translations as any)[language].home.marketingTools
  }, [language])

  const categories: Category[] = useMemo(() => homeContent.categories.map((cat: any) => ({
    id: cat.id,
    name: cat.name,
    icon: cat.id === 'roas-roi' ? TrendingUp : 
          cat.id === 'influencer' ? Users :
          cat.id === 'budget' ? DollarSign :
          cat.id === 'social' ? Share2 :
          cat.id === 'ecommerce' ? BarChart3 : BookOpen,
    description: cat.description
  })), [homeContent])


  const tools: Record<string, Tool[]> = useMemo(() => ({
    'roas-roi': [
      {
        id: 'ecommerce-economics',
        name: language === 'tr' ? 'E-Ticaret Ekonomisi' : language === 'en' ? 'E-Commerce Economics' : 'Экономика электронной коммерции',
        formula: language === 'tr' ? 'Kapsamlı e-ticaret karlılık analizi' : language === 'en' ? 'Comprehensive e-commerce profitability analysis' : 'Комплексный анализ рентабельности электронной коммерции',
        inputs: [],
        calculate: () => 0,
        description: language === 'tr' ? 'Kapsamlı e-ticaret ekonomisi ve ROAS hesaplama aracı' : language === 'en' ? 'Comprehensive e-commerce economics and ROAS calculator' : 'Комплексный калькулятор экономики электронной коммерции и ROAS'
      },
      {
        id: 'roas',
        name: language === 'tr' ? 'ROAS Hesaplama' : language === 'en' ? 'ROAS Calculator' : 'Калькулятор ROAS',
        formula: language === 'tr' ? 'ROAS = Gelir / Reklam Harcaması' : language === 'en' ? 'ROAS = Revenue / Ad Spend' : 'ROAS = Доход / Расходы на рекламу',
        inputs: [
          { id: 'revenue', label: 'Gelir (₺)', placeholder: '0' },
          { id: 'adSpend', label: 'Reklam Harcaması (₺)', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.revenue || !values.adSpend || values.adSpend === 0) return 0
          return values.revenue / values.adSpend
        },
        description: 'Return on Ad Spend - Reklam harcamasının geri dönüşü'
      },
      {
        id: 'roi',
        name: language === 'tr' ? 'ROI Hesaplama' : language === 'en' ? 'ROI Calculator' : 'Калькулятор ROI',
        formula: language === 'tr' ? 'ROI = ((Gelir - Reklam Harcaması) / Reklam Harcaması) × 100' : language === 'en' ? 'ROI = ((Revenue - Ad Spend) / Ad Spend) × 100' : 'ROI = ((Доход - Расходы на рекламу) / Расходы на рекламу) × 100',
        inputs: [
          { id: 'revenue', label: language === 'tr' ? 'Gelir (₺)' : language === 'en' ? 'Revenue (₺)' : 'Доход (₺)', placeholder: '0' },
          { id: 'adSpend', label: language === 'tr' ? 'Reklam Harcaması (₺)' : language === 'en' ? 'Ad Spend (₺)' : 'Расходы на рекламу (₺)', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.revenue || !values.adSpend || values.adSpend === 0) return 0
          return ((values.revenue - values.adSpend) / values.adSpend) * 100
        },
        unit: '%',
        description: language === 'tr' ? 'Return on Investment - Yatırım getirisi' : language === 'en' ? 'Return on Investment - Investment return' : 'Возврат инвестиций - Доходность инвестиций'
      },
      {
        id: 'cpm',
        name: language === 'tr' ? 'CPM Hesaplama' : language === 'en' ? 'CPM Calculator' : 'Калькулятор CPM',
        formula: language === 'tr' ? 'CPM = (Reklam Harcaması / Görüntülenme) × 1000' : language === 'en' ? 'CPM = (Ad Spend / Impressions) × 1000' : 'CPM = (Расходы на рекламу / Показы) × 1000',
        inputs: [
          { id: 'adSpend', label: language === 'tr' ? 'Reklam Harcaması (₺)' : language === 'en' ? 'Ad Spend (₺)' : 'Расходы на рекламу (₺)', placeholder: '0' },
          { id: 'impressions', label: language === 'tr' ? 'Görüntülenme' : language === 'en' ? 'Impressions' : 'Показы', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.adSpend || !values.impressions || values.impressions === 0) return 0
          return (values.adSpend / values.impressions) * 1000
        },
        unit: '₺',
        description: language === 'tr' ? 'Cost Per Mille - Bin görüntülenme başına maliyet' : language === 'en' ? 'Cost Per Mille - Cost per thousand impressions' : 'Стоимость за тысячу показов - Стоимость за тысячу показов'
      }
    ],
    'influencer': [
      {
        id: 'engagement-rate',
        name: 'Etkileşim Oranı',
        formula: 'Etkileşim Oranı = (Etkileşim / Takipçi) × 100',
        inputs: [
          { id: 'engagement', label: 'Etkileşim Sayısı', placeholder: '0' },
          { id: 'followers', label: 'Takipçi Sayısı', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.engagement || !values.followers || values.followers === 0) return 0
          return (values.engagement / values.followers) * 100
        },
        unit: '%',
        description: 'Influencer etkileşim performansı'
      },
      {
        id: 'cpm-influencer',
        name: 'Influencer CPM',
        formula: 'CPM = (Ödeme / Görüntülenme) × 1000',
        inputs: [
          { id: 'payment', label: 'Ödeme (₺)', placeholder: '0' },
          { id: 'reach', label: 'Erişim', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.payment || !values.reach || values.reach === 0) return 0
          return (values.payment / values.reach) * 1000
        },
        unit: '₺',
        description: 'Influencer kampanya maliyet analizi'
      }
    ],
    'budget': [
      {
        id: 'daily-budget',
        name: 'Günlük Bütçe',
        formula: 'Günlük Bütçe = Aylık Bütçe / Ay Gün Sayısı',
        inputs: [],
        calculate: () => 0,
        unit: '₺',
        description: 'Aylık, haftalık veya saatlik bütçe hesaplama'
      },
      {
        id: 'budget-allocation',
        name: 'Bütçe Dağılımı',
        formula: 'Kampanya Bütçesi = Toplam Bütçe × Yüzde',
        inputs: [],
        calculate: () => 0,
        unit: '₺',
        description: 'Toplam bütçeden kampanya bütçesi hesaplama'
      },
      {
        id: 'platform-budget',
        name: 'Platform Bazlı Bütçe Dağılımı',
        formula: 'Platform Bütçesi = Toplam Bütçe × Platform Yüzdesi',
        inputs: [],
        calculate: () => 0,
        unit: '₺',
        description: 'Çoklu platform için bütçe dağılımı ve optimizasyon'
      },
      {
        id: 'roi-budget',
        name: 'ROI Hedefli Bütçe',
        formula: 'Gerekli Bütçe = Hedef Gelir / (ROI / 100 + 1)',
        inputs: [],
        calculate: () => 0,
        unit: '₺',
        description: 'ROI hedefine göre gerekli bütçe hesaplama'
      }
    ],
    'social': [
      {
        id: 'engagement-rate-social',
        name: 'Etkileşim Oranı',
        formula: 'Etkileşim Oranı = (Etkileşim / Erişim) × 100',
        inputs: [
          { id: 'engagement', label: 'Etkileşim', placeholder: '0' },
          { id: 'reach', label: 'Erişim', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.engagement || !values.reach || values.reach === 0) return 0
          return (values.engagement / values.reach) * 100
        },
        unit: '%',
        description: 'Sosyal medya gönderi etkileşim performansı'
      },
      {
        id: 'ctr',
        name: 'Tıklama Oranı (CTR)',
        formula: 'CTR = (Tıklama / Görüntülenme) × 100',
        inputs: [
          { id: 'clicks', label: 'Tıklama', placeholder: '0' },
          { id: 'impressions', label: 'Görüntülenme', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.clicks || !values.impressions || values.impressions === 0) return 0
          return (values.clicks / values.impressions) * 100
        },
        unit: '%',
        description: 'Click Through Rate - Tıklama oranı'
      }
    ],
    'ecommerce': [
      {
        id: 'ltv',
        name: 'Müşteri Yaşam Boyu Değeri (LTV)',
        formula: 'LTV = Ortalama Sipariş Değeri × Sipariş Sıklığı × Müşteri Yaşam Süresi',
        inputs: [
          { id: 'avgOrder', label: 'Ortalama Sipariş Değeri (₺)', placeholder: '0' },
          { id: 'frequency', label: 'Sipariş Sıklığı', placeholder: '0' },
          { id: 'lifespan', label: 'Müşteri Yaşam Süresi (Ay)', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.avgOrder || !values.frequency || !values.lifespan) return 0
          return values.avgOrder * values.frequency * values.lifespan
        },
        unit: '₺',
        description: 'Müşteri yaşam boyu değeri hesaplama'
      },
      {
        id: 'cac',
        name: 'Müşteri Kazanma Maliyeti (CAC)',
        formula: 'CAC = Pazarlama Harcaması / Yeni Müşteri',
        inputs: [
          { id: 'marketingSpend', label: 'Pazarlama Harcaması (₺)', placeholder: '0' },
          { id: 'newCustomers', label: 'Yeni Müşteri Sayısı', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.marketingSpend || !values.newCustomers || values.newCustomers === 0) return 0
          return values.marketingSpend / values.newCustomers
        },
        unit: '₺',
        description: 'Customer Acquisition Cost - Müşteri kazanma maliyeti'
      },
      {
        id: 'ltv-cac',
        name: 'LTV/CAC Oranı',
        formula: 'LTV/CAC = LTV / CAC',
        inputs: [
          { id: 'ltv', label: 'LTV Değeri (₺)', placeholder: '0' },
          { id: 'cac', label: 'CAC Değeri (₺)', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.ltv || !values.cac || values.cac === 0) return 0
          return values.ltv / values.cac
        },
        description: 'LTV/CAC oranı - Müşteri karlılık analizi'
      }
    ],
    'content': [
      {
        id: 'content-roi',
        name: 'İçerik ROI',
        formula: 'İçerik ROI = ((Gelir - İçerik Maliyeti) / İçerik Maliyeti) × 100',
        inputs: [
          { id: 'revenue', label: 'Gelir (₺)', placeholder: '0' },
          { id: 'contentCost', label: 'İçerik Maliyeti (₺)', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.revenue || !values.contentCost || values.contentCost === 0) return 0
          return ((values.revenue - values.contentCost) / values.contentCost) * 100
        },
        unit: '%',
        description: 'İçerik pazarlama yatırım getirisi'
      },
      {
        id: 'content-engagement',
        name: 'İçerik Etkileşim Skoru',
        formula: 'Skor = (Beğeni × 1 + Yorum × 2 + Paylaşım × 3) / Erişim × 100',
        inputs: [
          { id: 'likes', label: 'Beğeni', placeholder: '0' },
          { id: 'comments', label: 'Yorum', placeholder: '0' },
          { id: 'shares', label: 'Paylaşım', placeholder: '0' },
          { id: 'reach', label: 'Erişim', placeholder: '0' }
        ],
        calculate: (values) => {
          if (!values.reach || values.reach === 0) return 0
          const score = (values.likes || 0) * 1 + (values.comments || 0) * 2 + (values.shares || 0) * 3
          return (score / values.reach) * 100
        },
        unit: '%',
        description: 'İçerik etkileşim performans skoru'
      }
    ]
  }), [language])

  const currentTools = tools[selectedCategory] || []
  const currentTool = currentTools.find(t => t.id === selectedTool) || currentTools[0]

  const handleInputChange = (inputId: string, value: string) => {
    const numValue = value === '' ? NaN : parseFloat(value)
    setInputValues(prev => ({
      ...prev,
      [inputId]: numValue
    }))
    
    if (currentTool) {
      const newValues = { ...inputValues, [inputId]: numValue }
      const calculated = currentTool.calculate(newValues)
      setResult(calculated)
    }
  }

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId)
    const firstTool = tools[categoryId]?.[0]
    if (firstTool) {
      setSelectedTool(firstTool.id)
      setInputValues({})
      setResult(null)
    }
  }

  const handleToolChange = (toolId: string) => {
    setSelectedTool(toolId)
    setInputValues({})
    setResult(null)
  }

  const handleDownloadPDF = () => {
    // PDF indirme simülasyonu
    alert('PDF raporu indiriliyor...')
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${currentTool.name} Sonucu`,
        text: `${currentTool.name}: ${result?.toFixed(2)}${currentTool.unit || ''}`,
        url: window.location.href
      }).catch(() => {})
    } else {
      navigator.clipboard.writeText(`${currentTool.name}: ${result?.toFixed(2)}${currentTool.unit || ''}`)
      alert('Sonuç panoya kopyalandı!')
    }
  }

  const tips = [
    {
      category: 'ROAS için',
      text: '4.0 ve üzeri ROAS değeri hedeflenebilir ve genellikle başarılı kabul edilir.',
      color: 'bg-blue-50 border-blue-200 text-blue-800'
    },
    {
      category: 'Etkileşim Oranı için',
      text: '%3-6 arası etkileşim oranı Instagram için ideal bir aralık olarak görülebilir.',
      color: 'bg-green-50 border-green-200 text-green-800'
    },
    {
      category: 'Bütçe Dağılımı için',
      text: 'Test bütçesi ayırarak performansı ölçmeniz önerilir.',
      color: 'bg-purple-50 border-purple-200 text-purple-800'
    },
    {
      category: 'LTV için',
      text: 'LTV/CAC oranının 3:1 veya üzeri olması önerilir.',
      color: 'bg-orange-50 border-orange-200 text-orange-800'
    }
  ]

  // SEO Meta Tags
  const seoTitle = selectedTool === 'ecommerce-economics' 
    ? 'ROI ve ROAS Hesaplama Aracı - E-Ticaret Ekonomisi Hesaplayıcı | Thinklyze'
    : `${pageContent.title} - Thinklyze`
  
  const seoDescription = selectedTool === 'ecommerce-economics'
    ? 'ROI, ROAS, LTV, CAC ve e-ticaret karlılık metriklerini hesaplayın. Ücretsiz hesaplama aracı ile kampanya performansınızı analiz edin. PDF rapor indirin.'
    : pageContent.subtitle
  
  const seoKeywords = selectedTool === 'ecommerce-economics'
    ? 'roi hesaplama, roas hesaplama, ltv hesaplama, cac hesaplama, e-ticaret karlılık, reklam verimliliği, yatırım getiri oranı, reklam getiri oranı'
    : 'pazarlama araçları, hesaplama araçları, dijital pazarlama'

  // Schema Markup for Calculator
  const calculatorSchema = selectedTool === 'ecommerce-economics' ? {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "E-Ticaret Ekonomisi Hesaplama Aracı",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "TRY"
    },
    "description": "ROI, ROAS, LTV, CAC ve e-ticaret karlılık metriklerini hesaplayan ücretsiz online hesaplama aracı",
    "url": "https://thinklyze.com/marketing-tools",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150"
    }
  } : null

  const howToSchema = selectedTool === 'ecommerce-economics' ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "ROI ve ROAS Nasıl Hesaplanır?",
    "description": "E-ticaret ekonomisi metriklerini hesaplama rehberi",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Ürün Satış Fiyatını Girin",
        "text": "Ürün satış fiyatını ve KDV oranını girin"
      },
      {
        "@type": "HowToStep",
        "name": "Maliyet Bilgilerini Girin",
        "text": "COGS, komisyon, kargo ve diğer maliyetleri girin"
      },
      {
        "@type": "HowToStep",
        "name": "Kâr Hedeflerini Belirleyin",
        "text": "Net kâr hedefinizi ve CAC hedefinizi belirleyin"
      },
      {
        "@type": "HowToStep",
        "name": "Sonuçları Görüntüleyin",
        "text": "ROI, ROAS, LTV ve diğer metrikleri görüntüleyin ve PDF rapor indirin"
      }
    ]
  } : null

  // FAQ Schema Markup
  const faqSchema = selectedTool === 'ecommerce-economics' ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "ROI ve ROAS nedir? Aralarındaki fark nedir?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ROI (Return on Investment) yatırım getiri oranıdır ve tüm maliyetleri içerir. ROAS (Return on Ad Spend) ise sadece reklam harcamasını içerir. ROI yüzde olarak ifade edilir (örn: %250), ROAS ise oran olarak ifade edilir (örn: 4:1)."
        }
      },
      {
        "@type": "Question",
        "name": "ROAS değeri ne kadar olmalı?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ROAS değeri sektöre ve ürün kâr marjına göre değişir. Genellikle e-ticaret işletmeleri için 4:1 veya üzeri ROAS değerleri hedeflenir. Break-even noktası 1:1'dir."
        }
      },
      {
        "@type": "Question",
        "name": "CAC (Müşteri Edinme Maliyeti) nasıl hesaplanır?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Bu hesaplayıcıda CAC, Brüt Kâr - Net Kâr formülü ile otomatik olarak hesaplanır. CAC, bir müşteriyi kazanmak için harcanan toplam maliyettir."
        }
      },
      {
        "@type": "Question",
        "name": "LTV (Lifetime Value) nedir? Nasıl hesaplanır?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "LTV, bir müşterinin işletmeye yaşam boyu sağladığı toplam gelirdir. Bu hesaplayıcıda LTV, Sepet Ortalaması × Müşteri Ortalama Sipariş Sayısı formülü ile hesaplanır. LTV/CAC oranı 3:1 veya üzeri olması önerilir."
        }
      },
      {
        "@type": "Question",
        "name": "KDV neden hesaplamalara dahil edilmiyor?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "KDV, devlet adına tahsil edilen bir vergi olduğu için işletmenin kazancı değildir. Bu nedenle kârlılık analizlerine, maliyet ve ROAS hesaplarına dahil edilmez."
        }
      },
      {
        "@type": "Question",
        "name": "PDF raporu nasıl indirilir?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Hesaplama sonuçlarınızı PDF olarak indirmek için 'PDF Rapor İndir' butonuna tıklayın. PDF'de logo, tarih, tüm hesaplama sonuçları ve sayfa numaraları yer alır."
        }
      },
      {
        "@type": "Question",
        "name": "Break-even ROAS nedir?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Break-even ROAS, kârın sıfır olduğu noktadır. Yani bu ROAS değerinde ne kâr ne de zarar edersiniz. Bu değerin üzerinde ROAS elde etmeniz gerekir."
        }
      },
      {
        "@type": "Question",
        "name": "Bu hesaplayıcı hangi metrikleri hesaplar?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Bu hesaplayıcı ROI, ROAS, LTV, CAC, Brüt Kâr, Net Kâr, Break-even ROAS ve kâr yüzdelerini hesaplar. Ayrıca komisyon, ödeme altyapı maliyeti, kargo, paketleme ve iade maliyetlerini de hesaba katar."
        }
      }
    ]
  } : null

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <link rel="canonical" href="https://thinklyze.com/marketing-tools" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thinklyze.com/marketing-tools" />
        <meta property="og:image" content="https://thinklyze.com/images/blog1.webp" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://thinklyze.com/images/blog1.webp" />
        
      </Helmet>
      
      {/* Schema Markup - Added via useEffect */}
      {calculatorSchema && (
        <script 
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(calculatorSchema) }}
        />
      )}
      {howToSchema && (
        <script 
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
        />
      )}
      {faqSchema && (
        <script 
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      
      <div className="pt-16 bg-[#f9f9f9] min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">

          {/* Category Selection - Horizontal at Top */}
          <div className="mb-6">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h2 className="text-xs sm:text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                {pageContent.selectCategory}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
                {categories.map((category) => {
                  const Icon = category.icon
                  const isActive = selectedCategory === category.id
                  return (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryChange(category.id)}
                      className={`flex flex-col items-center gap-1.5 sm:gap-2 p-2 sm:p-3 rounded-lg transition-all border-2 ${
                        isActive
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'bg-gray-50 border-transparent text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                      <div className="text-center">
                        <div className="font-medium text-xs sm:text-sm leading-tight">{category.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5 hidden lg:block leading-tight">{category.description}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Main Content Area - Full Width */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 lg:p-8">
              {currentTool && (
                <>
                  {/* Tabs */}
                  <div className="flex gap-2 mb-4 border-b border-gray-200 overflow-x-auto">
                    {currentTools.map((tool) => (
                      <button
                        key={tool.id}
                        onClick={() => handleToolChange(tool.id)}
                        className={`px-4 py-2 font-medium text-sm transition-colors whitespace-nowrap ${
                          selectedTool === tool.id
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {tool.name}
                      </button>
                    ))}
                  </div>

                  {/* E-Commerce Economics Tool */}
                  {selectedTool === 'ecommerce-economics' ? (
                    <ECommerceEconomicsCalculator 
                      inputValues={inputValues}
                      setInputValues={setInputValues}
                      vatRate={vatRate}
                      setVatRate={setVatRate}
                      pageContent={pageContent}
                      language={language}
                    />
                  ) : selectedTool === 'daily-budget' ? (
                    <DailyBudgetCalculator 
                      inputValues={inputValues}
                      setInputValues={setInputValues}
                    />
                  ) : selectedTool === 'budget-allocation' ? (
                    <BudgetAllocationCalculator 
                      inputValues={inputValues}
                      setInputValues={setInputValues}
                    />
                  ) : selectedTool === 'platform-budget' ? (
                    <PlatformBudgetCalculator 
                      inputValues={inputValues}
                      setInputValues={setInputValues}
                    />
                  ) : selectedTool === 'roi-budget' ? (
                    <ROIBudgetCalculator 
                      inputValues={inputValues}
                      setInputValues={setInputValues}
                    />
                  ) : (
                    /* Regular Tool Content */
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentTool.name}</h2>
                      <p className="text-gray-600 mb-6">{currentTool.description}</p>

                      {/* Formula Box */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-blue-900 mb-1">Formül:</div>
                          <div className="text-blue-800 font-mono text-sm">{currentTool.formula}</div>
                        </div>
                      </div>

                      {/* Input Fields and Result */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        {/* Input Fields */}
                        <div className="lg:col-span-2 space-y-4">
                          {currentTool.inputs.map((input) => (
                            <div key={input.id}>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {input.label}
                              </label>
                              <input
                                type="number"
                                value={inputValues[input.id] || ''}
                                onChange={(e) => handleInputChange(input.id, e.target.value)}
                                placeholder={input.placeholder}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                          ))}
                        </div>

                        {/* Result */}
                        <div className="lg:col-span-1">
                          <div className="bg-gray-50 rounded-lg p-6 text-center h-full flex flex-col justify-center">
                            <BarChart3 className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                            <div className="text-sm text-gray-600 mb-2">Sonuç</div>
                            <div className="text-4xl font-bold text-blue-600 mb-1">
                              {result !== null ? result.toFixed(2) : '0'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {currentTool.name.replace(' Hesaplama', '').replace(' Hesaplama', '')} {currentTool.unit || ''}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        <button
                          onClick={handleDownloadPDF}
                          className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          {pageContent?.buttons?.downloadPDF || 'PDF Rapor İndir'}
                        </button>
                        <button
                          onClick={handleShare}
                          className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                        >
                          <Share className="w-4 h-4" />
                          {pageContent?.buttons?.shareResult || 'Sonucu Paylaş'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
          </div>

          {/* Tips Section */}
          <div className="mt-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">İpuçları</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {tips.map((tip, index) => (
                <div
                  key={index}
                  className={`${tip.color} border rounded-lg p-4`}
                >
                  <div className="font-semibold text-sm mb-1">{tip.category}</div>
                  <div className="text-sm">{tip.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// Budget Calculator Components
interface BudgetCalculatorProps {
  inputValues: Record<string, number>
  setInputValues: (values: Record<string, number> | ((prev: Record<string, number>) => Record<string, number>)) => void
}

// Daily Budget Calculator
const DailyBudgetCalculator = ({ inputValues, setInputValues }: BudgetCalculatorProps) => {
  const [budgetType, setBudgetType] = useState<'monthly' | 'weekly' | 'hourly'>('monthly')
  const [daysInMonth, setDaysInMonth] = useState<number>(30)
  const [workingDays, setWorkingDays] = useState<number>(22)
  const [hoursPerDay, setHoursPerDay] = useState<number>(24)

  const handleInputChange = (fieldId: string, value: string) => {
    const numValue = parseFloat(value) || 0
    setInputValues(prev => ({
      ...prev,
      [fieldId]: numValue
    }))
  }

  const monthlyBudget = inputValues.monthlyBudget || 0
  const weeklyBudget = inputValues.weeklyBudget || 0
  const dailyBudget = budgetType === 'monthly' 
    ? (monthlyBudget / daysInMonth)
    : budgetType === 'weekly'
    ? (weeklyBudget / 7)
    : 0
  
  const hourlyBudget = budgetType === 'monthly'
    ? (monthlyBudget / (daysInMonth * hoursPerDay))
    : budgetType === 'weekly'
    ? (weeklyBudget / (7 * hoursPerDay))
    : (dailyBudget / hoursPerDay)
  
  const workingDayBudget = budgetType === 'monthly'
    ? (monthlyBudget / workingDays)
    : budgetType === 'weekly'
    ? (weeklyBudget / 5)
    : 0

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Günlük Bütçe Hesaplama</h2>
        <p className="text-gray-600 mb-6">Aylık, haftalık veya saatlik bütçe hesaplama aracı</p>
      </div>

      {/* Budget Type Selection */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="text-sm font-medium text-blue-900 mb-3">Bütçe Tipi Seçin:</div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setBudgetType('monthly')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              budgetType === 'monthly'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Aylık
          </button>
          <button
            onClick={() => setBudgetType('weekly')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              budgetType === 'weekly'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Haftalık
          </button>
          <button
            onClick={() => setBudgetType('hourly')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              budgetType === 'hourly'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Saatlik
          </button>
        </div>
      </div>

      {/* Input Fields */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          {budgetType === 'monthly' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aylık Bütçe (₺)
                </label>
                <input
                  type="number"
                  value={monthlyBudget || ''}
                  onChange={(e) => handleInputChange('monthlyBudget', e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ay Gün Sayısı
                </label>
                <select
                  value={daysInMonth}
                  onChange={(e) => setDaysInMonth(Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={28}>28 Gün</option>
                  <option value={29}>29 Gün</option>
                  <option value={30}>30 Gün</option>
                  <option value={31}>31 Gün</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Çalışma Günü Sayısı (Ay)
                </label>
                <input
                  type="number"
                  value={workingDays}
                  onChange={(e) => setWorkingDays(Number(e.target.value))}
                  placeholder="22"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </>
          )}
          {budgetType === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Haftalık Bütçe (₺)
              </label>
              <input
                type="number"
                value={weeklyBudget || ''}
                onChange={(e) => handleInputChange('weeklyBudget', e.target.value)}
                placeholder="0"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Günlük Saat Sayısı
            </label>
            <input
              type="number"
              value={hoursPerDay}
              onChange={(e) => setHoursPerDay(Number(e.target.value))}
              placeholder="24"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="text-sm text-gray-600 mb-2">Günlük Bütçe</div>
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {dailyBudget.toFixed(2)} ₺
            </div>
          </div>
          {budgetType === 'monthly' && (
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="text-sm text-gray-600 mb-2">Çalışma Günü Bütçesi</div>
              <div className="text-3xl font-bold text-green-600 mb-1">
                {workingDayBudget.toFixed(2)} ₺
              </div>
            </div>
          )}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="text-sm text-gray-600 mb-2">Saatlik Bütçe</div>
            <div className="text-3xl font-bold text-purple-600 mb-1">
              {hourlyBudget.toFixed(2)} ₺
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Budget Allocation Calculator
const BudgetAllocationCalculator = ({ inputValues, setInputValues }: BudgetCalculatorProps) => {
  const [campaigns, setCampaigns] = useState<Array<{ id: string; name: string; percentage: number }>>([
    { id: '1', name: 'Kampanya 1', percentage: 0 }
  ])

  const handleInputChange = (fieldId: string, value: string) => {
    const numValue = parseFloat(value) || 0
    setInputValues(prev => ({
      ...prev,
      [fieldId]: numValue
    }))
  }

  const handleCampaignChange = (id: string, field: 'name' | 'percentage', value: string | number) => {
    setCampaigns(prev => prev.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ))
  }

  const addCampaign = () => {
    setCampaigns(prev => [...prev, { 
      id: Date.now().toString(), 
      name: `Kampanya ${prev.length + 1}`, 
      percentage: 0 
    }])
  }

  const removeCampaign = (id: string) => {
    setCampaigns(prev => prev.filter(c => c.id !== id))
  }

  const totalBudget = inputValues.totalBudget || 0
  const totalPercentage = campaigns.reduce((sum, c) => sum + c.percentage, 0)
  const isValid = totalPercentage <= 100

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Bütçe Dağılımı</h2>
        <p className="text-gray-600 mb-6">Toplam bütçeden kampanya bütçesi hesaplama</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Toplam Bütçe (₺)
            </label>
            <input
              type="number"
              value={totalBudget || ''}
              onChange={(e) => handleInputChange('totalBudget', e.target.value)}
              placeholder="0"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Kampanyalar</label>
              <button
                onClick={addCampaign}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                + Kampanya Ekle
              </button>
            </div>
            {campaigns.map((campaign) => (
              <div key={campaign.id} className="flex gap-3 items-end">
                <div className="flex-1">
                  <input
                    type="text"
                    value={campaign.name}
                    onChange={(e) => handleCampaignChange(campaign.id, 'name', e.target.value)}
                    placeholder="Kampanya adı"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div className="w-24">
                  <input
                    type="number"
                    value={campaign.percentage || ''}
                    onChange={(e) => handleCampaignChange(campaign.id, 'percentage', parseFloat(e.target.value) || 0)}
                    placeholder="%"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                {campaigns.length > 1 && (
                  <button
                    onClick={() => removeCampaign(campaign.id)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {!isValid && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                Toplam yüzde %100'ü geçemez. Şu an: %{totalPercentage.toFixed(1)}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm font-medium text-blue-900 mb-3">Bütçe Dağılımı</div>
            <div className="space-y-2">
              {campaigns.map((campaign) => {
                const budget = (totalBudget * campaign.percentage) / 100
                return (
                  <div key={campaign.id} className="bg-white rounded-lg p-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-900">{campaign.name}</span>
                      <span className="text-sm text-gray-600">%{campaign.percentage}</span>
                    </div>
                    <div className="text-lg font-bold text-blue-600">
                      {budget.toFixed(2)} ₺
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-blue-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-blue-900">Kalan Bütçe</span>
                <span className="text-lg font-bold text-blue-600">
                  {(totalBudget - campaigns.reduce((sum, c) => sum + (totalBudget * c.percentage) / 100, 0)).toFixed(2)} ₺
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Platform Budget Calculator
const PlatformBudgetCalculator = ({ inputValues, setInputValues }: BudgetCalculatorProps) => {
  const platforms = [
    { id: 'google', name: 'Google Ads', percentage: 0, color: 'bg-blue-500' },
    { id: 'meta', name: 'Meta Ads', percentage: 0, color: 'bg-blue-600' },
    { id: 'tiktok', name: 'TikTok Ads', percentage: 0, color: 'bg-black' },
    { id: 'linkedin', name: 'LinkedIn Ads', percentage: 0, color: 'bg-blue-700' },
    { id: 'twitter', name: 'Twitter Ads', percentage: 0, color: 'bg-blue-400' },
    { id: 'other', name: 'Diğer', percentage: 0, color: 'bg-gray-500' }
  ]

  const [platformBudgets, setPlatformBudgets] = useState(platforms)

  const handleInputChange = (fieldId: string, value: string) => {
    const numValue = parseFloat(value) || 0
    setInputValues(prev => ({
      ...prev,
      [fieldId]: numValue
    }))
  }

  const handlePlatformChange = (id: string, percentage: number) => {
    setPlatformBudgets(prev => prev.map(p => 
      p.id === id ? { ...p, percentage } : p
    ))
  }

  const totalBudget = inputValues.totalBudget || 0
  const totalPercentage = platformBudgets.reduce((sum, p) => sum + p.percentage, 0)
  const isValid = totalPercentage <= 100

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Platform Bazlı Bütçe Dağılımı</h2>
        <p className="text-gray-600 mb-6">Çoklu platform için bütçe dağılımı ve optimizasyon</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Toplam Bütçe (₺)
            </label>
            <input
              type="number"
              value={totalBudget || ''}
              onChange={(e) => handleInputChange('totalBudget', e.target.value)}
              placeholder="0"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform Bütçe Dağılımı (%)
            </label>
            {platformBudgets.map((platform) => {
              const budget = (totalBudget * platform.percentage) / 100
              return (
                <div key={platform.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${platform.color}`}></div>
                      <span className="text-sm font-medium text-gray-700">{platform.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={platform.percentage || ''}
                        onChange={(e) => handlePlatformChange(platform.id, parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                  </div>
                  {totalBudget > 0 && (
                    <div className="text-xs text-gray-600 ml-5">
                      {budget.toFixed(2)} ₺
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {!isValid && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                Toplam yüzde %100'ü geçemez. Şu an: %{totalPercentage.toFixed(1)}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="text-sm font-medium text-gray-700 mb-4">Bütçe Özeti</div>
            <div className="space-y-3">
              {platformBudgets.map((platform) => {
                const budget = (totalBudget * platform.percentage) / 100
                if (platform.percentage === 0) return null
                return (
                  <div key={platform.id} className="bg-white rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${platform.color}`}></div>
                        <span className="text-sm font-medium text-gray-900">{platform.name}</span>
                      </div>
                      <span className="text-sm text-gray-600">%{platform.percentage}</span>
                    </div>
                    <div className="text-xl font-bold text-blue-600">
                      {budget.toFixed(2)} ₺
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Kalan Bütçe</span>
                <span className="text-lg font-bold text-gray-900">
                  {(totalBudget - platformBudgets.reduce((sum, p) => sum + (totalBudget * p.percentage) / 100, 0)).toFixed(2)} ₺
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ROI Budget Calculator
const ROIBudgetCalculator = ({ inputValues, setInputValues }: BudgetCalculatorProps) => {
  const handleInputChange = (fieldId: string, value: string) => {
    const numValue = parseFloat(value) || 0
    setInputValues(prev => ({
      ...prev,
      [fieldId]: numValue
    }))
  }

  const targetRevenue = inputValues.targetRevenue || 0
  const targetROI = inputValues.targetROI || 0
  const requiredBudget = targetROI > 0 ? targetRevenue / (targetROI / 100 + 1) : 0
  const expectedProfit = targetRevenue - requiredBudget
  const actualROI = requiredBudget > 0 ? ((targetRevenue - requiredBudget) / requiredBudget) * 100 : 0

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">ROI Hedefli Bütçe Hesaplama</h2>
        <p className="text-gray-600 mb-6">ROI hedefine göre gerekli bütçe hesaplama</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="text-sm font-medium text-blue-900 mb-1">Formül:</div>
        <div className="text-blue-800 font-mono text-sm">
          Gerekli Bütçe = Hedef Gelir / (ROI / 100 + 1)
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hedef Gelir (₺)
            </label>
            <input
              type="number"
              value={targetRevenue || ''}
              onChange={(e) => handleInputChange('targetRevenue', e.target.value)}
              placeholder="0"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hedef ROI (%)
            </label>
            <input
              type="number"
              value={targetROI || ''}
              onChange={(e) => handleInputChange('targetROI', e.target.value)}
              placeholder="0"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="text-sm text-gray-600 mb-2">Gerekli Bütçe</div>
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {requiredBudget.toFixed(2)} ₺
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="text-sm text-gray-600 mb-2">Beklenen Kar</div>
            <div className="text-3xl font-bold text-green-600 mb-1">
              {expectedProfit.toFixed(2)} ₺
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="text-sm text-gray-600 mb-2">Gerçek ROI</div>
            <div className="text-3xl font-bold text-purple-600 mb-1">
              {actualROI.toFixed(2)} %
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// E-Commerce Economics Calculator Component
interface ECommerceEconomicsCalculatorProps {
  inputValues: Record<string, number>
  setInputValues: (values: Record<string, number> | ((prev: Record<string, number>) => Record<string, number>)) => void
  vatRate: number
  setVatRate: (rate: number) => void
  pageContent: any
  language: string
}

const ECommerceEconomicsCalculator = ({ 
  inputValues, 
  setInputValues, 
  vatRate, 
  setVatRate,
  pageContent,
  language
}: ECommerceEconomicsCalculatorProps) => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const calculatorRef = useRef<HTMLDivElement>(null)
  
  // FAQ Data - Use translations if available, otherwise fallback to Turkish
  const faqs = useMemo(() => {
    if (pageContent?.faq?.items && Array.isArray(pageContent.faq.items) && pageContent.faq.items.length > 0) {
      return pageContent.faq.items
    }
    // Fallback to Turkish if translations are not available
    return [
      {
        question: "ROI ve ROAS nedir? Aralarındaki fark nedir?",
        answer: "ROI (Return on Investment) yatırım getiri oranıdır ve tüm maliyetleri içerir. ROAS (Return on Ad Spend) ise sadece reklam harcamasını içerir. ROI yüzde olarak ifade edilir (örn: %250), ROAS ise oran olarak ifade edilir (örn: 4:1). Detaylı bilgi için <a href='/blog/roi-nedir-nasil-hesaplanir-2025-rehberi' class='text-blue-600 hover:text-blue-800 underline'>ROI rehberimizi</a> ve <a href='/blog/roas-nedir-nasil-hesaplanir-reklam-verimliligi-rehberi' class='text-blue-600 hover:text-blue-800 underline'>ROAS rehberimizi</a> okuyabilirsiniz."
      },
      {
        question: "ROAS değeri ne kadar olmalı?",
        answer: "ROAS değeri sektöre ve ürün kâr marjına göre değişir. Genellikle e-ticaret işletmeleri için 4:1 veya üzeri ROAS değerleri hedeflenir. Ancak ürün kâr marjı düşükse daha yüksek ROAS gerekebilir. Break-even noktası 1:1'dir, yani bu değerin üzerinde olması gerekir."
      },
      {
        question: "CAC (Müşteri Edinme Maliyeti) nasıl hesaplanır?",
        answer: "Bu hesaplayıcıda CAC, Brüt Kâr - Net Kâr formülü ile otomatik olarak hesaplanır. CAC, bir müşteriyi kazanmak için harcanan toplam maliyettir. CAC değeri ne kadar düşükse, işletme o kadar kârlıdır."
      },
      {
        question: "LTV (Lifetime Value) nedir? Nasıl hesaplanır?",
        answer: "LTV, bir müşterinin işletmeye yaşam boyu sağladığı toplam gelirdir. Bu hesaplayıcıda LTV, Sepet Ortalaması × Müşteri Ortalama Sipariş Sayısı formülü ile hesaplanır. LTV/CAC oranı 3:1 veya üzeri olması önerilir."
      },
      {
        question: "KDV neden hesaplamalara dahil edilmiyor?",
        answer: "KDV, devlet adına tahsil edilen bir vergi olduğu için işletmenin kazancı değildir. Bu nedenle kârlılık analizlerine, maliyet ve ROAS hesaplarına dahil edilmez. Hesaplayıcıda KDV dahil satış fiyatı gösterilir, ancak kâr hesaplamalarında KDV hariç tutulur."
      },
      {
        question: "PDF raporu nasıl indirilir?",
        answer: "Hesaplama sonuçlarınızı PDF olarak indirmek için 'PDF Rapor İndir' butonuna tıklayın. PDF'de logo, tarih, tüm hesaplama sonuçları ve sayfa numaraları yer alır. PDF'i paylaşmak için 'Sonucu Paylaş' butonunu da kullanabilirsiniz."
      },
      {
        question: "Hesaplama sonuçlarını nasıl paylaşabilirim?",
        answer: "Hesaplama sonuçlarınızı paylaşmak için 'Sonucu Paylaş' butonuna tıklayın. Bu buton, PDF raporunu paylaşmanıza veya sonuçları metin olarak kopyalamanıza olanak tanır. Web Share API destekleniyorsa PDF dosyası olarak paylaşabilirsiniz."
      },
      {
        question: "Hangi alanlar değiştirilebilir?",
        answer: "Hesaplayıcıda sadece yeşil renkli alanlar ve KDV oranı değiştirilebilir. Sarı renkli alanlar otomatik olarak hesaplanan sonuçlardır ve değiştirilemez. CAC değeri de otomatik hesaplanır (Brüt Kâr - Net Kâr) ve değiştirilemez."
      },
      {
        question: "Break-even ROAS nedir?",
        answer: "Break-even ROAS, kârın sıfır olduğu noktadır. Yani bu ROAS değerinde ne kâr ne de zarar edersiniz. Break-even ROAS değeri, toplam maliyetlerin gelire eşit olduğu noktadır. Bu değerin üzerinde ROAS elde etmeniz gerekir."
      },
      {
        question: "Bu hesaplayıcı hangi metrikleri hesaplar?",
        answer: "Bu hesaplayıcı ROI, ROAS, LTV, CAC, Brüt Kâr, Net Kâr, Break-even ROAS ve kâr yüzdelerini hesaplar. Ayrıca komisyon, ödeme altyapı maliyeti, kargo, paketleme ve iade maliyetlerini de hesaba katar."
      }
    ]
  }, [pageContent, language])

  const formatInputValue = (value?: number) => {
    if (value === undefined || Number.isNaN(value)) {
      return ''
    }
    return value
  }

  const getNumericValue = (value?: number) => {
    return value !== undefined && Number.isFinite(value) ? (value as number) : 0
  }

  const handleInputChange = (fieldId: string, value: string) => {
    const numValue = value === '' ? Number.NaN : parseFloat(value)
    setInputValues(prev => ({
      ...prev,
      [fieldId]: numValue
    }))
  }

  // Calculations
  const rawProductPrice = inputValues.productPrice
  const rawCogs = inputValues.cogs
  const rawWholesaleDepot = inputValues.wholesaleDepot
  const rawCommissionRatePercent = inputValues.commissionRatePercent
  const rawPaymentInfraCommissionRate = inputValues.paymentInfraCommissionRate
  const rawPackagingCost = inputValues.packagingCost
  const rawShippingCost = inputValues.shippingCost
  const rawReturnRate = inputValues.returnRate
  const rawNetProfitTarget = inputValues.netProfitTarget
  const rawAvgOrderCount = inputValues.avgOrderCount
  const rawAvgBasket = inputValues.avgBasket

  const productPrice = getNumericValue(rawProductPrice)
  const vatIncludedPrice = productPrice * (1 + vatRate / 100)
  const cogs = getNumericValue(rawCogs)
  const wholesaleDepot = getNumericValue(rawWholesaleDepot)
  const commissionRatePercent = getNumericValue(rawCommissionRatePercent)
  const commissionRate = (vatIncludedPrice * commissionRatePercent) / 100
  const paymentInfraCommissionRate = getNumericValue(rawPaymentInfraCommissionRate)
  const paymentInfraCost = paymentInfraCommissionRate > 0 ? (vatIncludedPrice * paymentInfraCommissionRate) / 100 : 0
  const packagingCost = getNumericValue(rawPackagingCost)
  const shippingCost = getNumericValue(rawShippingCost)
  const returnRate = getNumericValue(rawReturnRate)
  const returnCost = (vatIncludedPrice * returnRate) / 100
  const codExpenses = commissionRate + paymentInfraCost + packagingCost + shippingCost + returnCost
  const grossProfit = vatIncludedPrice - cogs - wholesaleDepot - codExpenses
  const netProfitTarget = Number.isFinite(rawNetProfitTarget) ? (rawNetProfitTarget as number) : 0
  // CAC = Brüt Kar - Net Kar
  const cacTarget = grossProfit >= netProfitTarget ? (grossProfit - netProfitTarget) : 0
  const avgOrderCount = getNumericValue(rawAvgOrderCount)
  const avgBasket = getNumericValue(rawAvgBasket)
  
  // Calculate total revenue: Use avgBasket if provided, otherwise use vatIncludedPrice
  // If avgOrderCount is provided, multiply by order count, otherwise assume 1 order
  const totalRevenue = avgBasket > 0 
    ? (avgBasket * (avgOrderCount || 1))
    : (vatIncludedPrice * (avgOrderCount || 1))
  
  // Performance Metrics
  // ROAS and ROI are calculated using CAC (Brüt Kar - Net Kar) as the ad spend
  const roas = cacTarget > 0 ? totalRevenue / cacTarget : 0
  const roi = cacTarget > 0 ? ((totalRevenue - cacTarget) / cacTarget) * 100 : 0
  const breakEvenRoas = grossProfit > 0 ? vatIncludedPrice / grossProfit : 0
  const ltv = avgBasket > 0 ? (avgBasket * avgOrderCount) : (vatIncludedPrice * avgOrderCount)
  const ltvCacRatio = cacTarget > 0 ? ltv / cacTarget : 0
  const netProfitPercent = vatIncludedPrice > 0 ? (netProfitTarget / vatIncludedPrice) * 100 : 0
  const grossProfitPercent = vatIncludedPrice > 0 ? (grossProfit / vatIncludedPrice) * 100 : 0

  // PDF Generation Function - Capture calculator as image
  // Returns PDF as Blob if shareMode is true, otherwise downloads it
  const generatePDF = async (shareMode: boolean = false): Promise<Blob | void> => {
    let loadingOverlay: HTMLDivElement | null = null
    let styleElement: HTMLStyleElement | null = null
    
    // Store original scroll and body styles at function scope (accessible in finally block)
    const originalBodyOverflow = document.body.style.overflow
    const originalBodyPosition = document.body.style.position
    const originalBodyWidth = document.body.style.width
    const originalBodyHeight = document.body.style.height
    const originalBodyTop = document.body.style.top
    const originalBodyLeft = document.body.style.left
    const originalBodyRight = document.body.style.right
    const originalBodyTouchAction = document.body.style.touchAction
    const originalBodyMaxWidth = document.body.style.maxWidth
    const originalHtmlOverflow = document.documentElement.style.overflow
    const originalHtmlPosition = document.documentElement.style.position
    const originalHtmlTouchAction = document.documentElement.style.touchAction
    const originalHtmlWidth = document.documentElement.style.width
    const originalHtmlMaxWidth = document.documentElement.style.maxWidth
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop || window.scrollY
    
    // IMMEDIATELY lock scroll before any other operations
    // This prevents any layout shifts during PDF generation
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.width = '100%'
    document.body.style.height = '100%'
    document.body.style.top = `-${scrollPosition}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.touchAction = 'none' // Prevent touch scrolling on mobile
    document.body.style.overscrollBehavior = 'none' // Prevent overscroll
    document.documentElement.style.overflow = 'hidden'
    document.documentElement.style.position = 'relative'
    document.documentElement.style.touchAction = 'none'
    document.documentElement.style.overscrollBehavior = 'none'
    
    // Force a reflow to ensure scroll lock is applied
    document.body.offsetHeight
    
    // Create full-screen overlay BEFORE any other operations
    // This completely covers the screen, making any layout shifts invisible
    loadingOverlay = document.createElement('div')
    loadingOverlay.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 999999;
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: all;
      user-select: none;
      will-change: opacity;
      animation: fadeIn 0.2s ease-out;
    `
    
    // Add fade-in and fade-out animations
    styleElement = document.createElement('style')
    styleElement.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `
    document.head.appendChild(styleElement)
    
    // Create loading content
    const loadingContent = document.createElement('div')
    loadingContent.style.cssText = `
      background: white;
      border-radius: 16px;
      padding: 32px 40px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      text-align: center;
      min-width: 280px;
      max-width: 90vw;
    `
    
    // Create spinner
    const spinner = document.createElement('div')
    spinner.style.cssText = `
      width: 48px;
      height: 48px;
      border: 4px solid #f3f4f6;
      border-top-color: #3b82f6;
      border-radius: 50%;
      margin: 0 auto 20px;
      animation: spin 0.8s linear infinite;
    `
    
    // Create text
    const text = document.createElement('div')
    text.style.cssText = `
      font-size: 18px;
      font-weight: 600;
      color: #111827;
      margin-bottom: 8px;
    `
    text.textContent = 'PDF oluşturuluyor...'
    
    // Create subtitle
    const subtitle = document.createElement('div')
    subtitle.style.cssText = `
      font-size: 14px;
      color: #6b7280;
      animation: pulse 2s ease-in-out infinite;
    `
    subtitle.textContent = 'Lütfen bekleyin'
    
    // Assemble loading content
    loadingContent.appendChild(spinner)
    loadingContent.appendChild(text)
    loadingContent.appendChild(subtitle)
    loadingOverlay.appendChild(loadingContent)
    
    // Append overlay to body
    document.body.appendChild(loadingOverlay)
    
    // Force reflow after overlay is added
    document.body.offsetHeight
    
    // Also prevent scrolling on window level
    const preventScroll = (e: Event) => {
      e.preventDefault()
      e.stopPropagation()
      return false
    }
    
    // Add event listeners to prevent all scroll events
    window.addEventListener('scroll', preventScroll, { passive: false, capture: true })
    window.addEventListener('wheel', preventScroll, { passive: false, capture: true })
    window.addEventListener('touchmove', preventScroll, { passive: false, capture: true })
    window.addEventListener('touchstart', preventScroll, { passive: false, capture: true })
    
    try {
      if (!calculatorRef.current) {
        alert('Hesaplayıcı bulunamadı. Lütfen sayfayı yenileyip tekrar deneyin.')
        return
      }

      try {
        // Capture calculator as image using html2canvas
        // Use fixed desktop width for consistent PDF output on mobile
        const element = calculatorRef.current
        
        // Store original styles
        const originalOverflow = element.style.overflow
        const originalHeight = element.style.height
        
        // Convert all input and select elements to divs for reliable PDF capture
        // html2canvas has issues capturing input values, so we convert them to divs
        const inputs = element.querySelectorAll('input, select')
        const inputReplacements: Array<{
          original: HTMLElement
          replacement: HTMLElement
          parent: HTMLElement | null
        }> = []
        
        inputs.forEach((input) => {
          const htmlInput = input as HTMLInputElement | HTMLSelectElement
          const parent = htmlInput.parentElement
          
          if (!parent) return
          
          // Get computed styles to preserve appearance
          const computedStyle = window.getComputedStyle(htmlInput)
          
          // Get the actual displayed value
          let displayValue = ''
          if (htmlInput instanceof HTMLSelectElement) {
            displayValue = htmlInput.options[htmlInput.selectedIndex]?.text || htmlInput.value || ''
          } else {
            // For inputs, use the value attribute (which reflects the state)
            displayValue = htmlInput.value || ''
            // If it's a number input with no value, show '0'
            if (!displayValue && htmlInput.type === 'number') {
              displayValue = '0'
            }
          }
          
          // Create replacement div with exact same appearance
          const replacement = document.createElement('div')
          replacement.textContent = displayValue
          replacement.className = htmlInput.className
          
          // Copy all computed styles to preserve visual appearance
          replacement.style.cssText = htmlInput.style.cssText
          replacement.style.padding = computedStyle.padding || '12px 16px'
          replacement.style.fontSize = computedStyle.fontSize || '14px'
          replacement.style.fontWeight = computedStyle.fontWeight
          replacement.style.fontFamily = computedStyle.fontFamily
          replacement.style.color = computedStyle.color
          replacement.style.backgroundColor = computedStyle.backgroundColor
          replacement.style.border = computedStyle.border
          replacement.style.borderWidth = computedStyle.borderWidth
          replacement.style.borderStyle = computedStyle.borderStyle
          replacement.style.borderColor = computedStyle.borderColor
          replacement.style.borderRadius = computedStyle.borderRadius
          replacement.style.width = computedStyle.width || '100%'
          replacement.style.height = 'auto'
          replacement.style.minHeight = '48px'
          replacement.style.lineHeight = '1.6'
          replacement.style.overflow = 'visible'
          replacement.style.textOverflow = 'clip'
          replacement.style.whiteSpace = 'normal'
          replacement.style.wordWrap = 'break-word'
          replacement.style.wordBreak = 'break-word'
          replacement.style.boxSizing = 'border-box'
          replacement.style.display = 'block'
          replacement.style.verticalAlign = 'middle'
          
          // Store replacement info
          inputReplacements.push({
            original: htmlInput,
            replacement: replacement,
            parent: parent
          })
          
          // Hide original input and insert replacement div
          htmlInput.style.display = 'none'
          parent.insertBefore(replacement, htmlInput)
        })
        
        // Adjust output divs for PDF
        const outputDivs = element.querySelectorAll('div[class*="bg-yellow-50"], div[class*="bg-green-50"]')
        const originalOutputStyles: Array<{
          element: HTMLElement
          padding: string
          fontSize: string
          height: string
          overflow: string
          textOverflow: string
          whiteSpace: string
          lineHeight: string
        }> = []
        
        outputDivs.forEach((div) => {
          const htmlDiv = div as HTMLElement
          const computedStyle = window.getComputedStyle(htmlDiv)
          
          originalOutputStyles.push({
            element: htmlDiv,
            padding: htmlDiv.style.padding,
            fontSize: htmlDiv.style.fontSize,
            height: htmlDiv.style.height,
            overflow: htmlDiv.style.overflow,
            textOverflow: htmlDiv.style.textOverflow,
            whiteSpace: htmlDiv.style.whiteSpace,
            lineHeight: htmlDiv.style.lineHeight
          })
          
          // Ensure full content is visible
          htmlDiv.style.padding = computedStyle.padding || '14px 18px'
          htmlDiv.style.fontSize = computedStyle.fontSize || '15px'
          htmlDiv.style.height = 'auto'
          htmlDiv.style.minHeight = 'auto'
          htmlDiv.style.lineHeight = '1.5'
          htmlDiv.style.overflow = 'visible'
          htmlDiv.style.textOverflow = 'clip'
          htmlDiv.style.whiteSpace = 'normal'
          htmlDiv.style.wordWrap = 'break-word'
        })
        
        // Store original styles for restoration
        const originalElementWidth = element.style.width
        const originalElementMaxWidth = element.style.maxWidth
        const originalElementPadding = element.style.padding
        const originalElementMargin = element.style.margin
        const originalElementMinWidth = element.style.minWidth
        
        // Force desktop viewport on body and html (critical for mobile)
        // Do this carefully to prevent layout shift - body is already fixed positioned
        // Store current scroll position before making width changes
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || window.scrollY
        
        // Apply width changes while body is fixed - this prevents visible layout shift
        document.body.style.width = '1400px'
        document.body.style.maxWidth = '1400px'
        document.body.style.overflow = 'visible'
        document.documentElement.style.width = '1400px'
        document.documentElement.style.maxWidth = '1400px'
        
        // Force reflow after width changes to ensure layout is stable
        document.body.offsetHeight
        await new Promise(resolve => requestAnimationFrame(resolve))
        
        // Force desktop layout: Set fixed width for desktop-like rendering
        element.style.width = '1200px'
        element.style.maxWidth = '1200px'
        element.style.minWidth = '1200px'
        element.style.margin = '0 auto'
        element.style.padding = '16px'
        
        // Also adjust grid columns to be 4 columns (desktop layout)
        const gridContainers = element.querySelectorAll('[class*="grid"], [class*="grid-cols"]')
        const gridRestore: Array<{ 
          element: HTMLElement
          originalGap: string
          originalClass: string
          originalDisplay: string
          originalGridTemplateColumns: string
          originalWidth: string
          originalMaxWidth: string
          originalMinWidth: string
        }> = []
        
        gridContainers.forEach((grid) => {
          const htmlGrid = grid as HTMLElement
          
          // Store original values
          gridRestore.push({
            element: htmlGrid,
            originalGap: htmlGrid.style.gap,
            originalClass: htmlGrid.className,
            originalDisplay: htmlGrid.style.display,
            originalGridTemplateColumns: htmlGrid.style.gridTemplateColumns,
            originalWidth: htmlGrid.style.width,
            originalMaxWidth: htmlGrid.style.maxWidth,
            originalMinWidth: htmlGrid.style.minWidth
          })
          
          // Force 4-column grid layout for desktop (override all responsive classes)
          htmlGrid.style.display = 'grid'
          htmlGrid.style.gridTemplateColumns = 'repeat(4, minmax(0, 1fr))'
          htmlGrid.style.gap = '10px'
          htmlGrid.style.width = '100%'
          htmlGrid.style.maxWidth = '100%'
          htmlGrid.style.minWidth = '0'
          // Remove any responsive width constraints
          htmlGrid.style.flexBasis = 'auto'
          htmlGrid.style.flexShrink = '1'
          htmlGrid.style.flexGrow = '0'
        })
        
        // Force all flex containers to not wrap (desktop behavior)
        const flexContainers = element.querySelectorAll('[class*="flex"]')
        const flexRestore: Array<{
          element: HTMLElement
          originalFlexWrap: string
          originalFlexDirection: string
        }> = []
        
        flexContainers.forEach((flex) => {
          const htmlFlex = flex as HTMLElement
          const computedStyle = window.getComputedStyle(htmlFlex)
          
          if (computedStyle.display === 'flex') {
            flexRestore.push({
              element: htmlFlex,
              originalFlexWrap: htmlFlex.style.flexWrap,
              originalFlexDirection: htmlFlex.style.flexDirection
            })
            
            // Force desktop flex behavior
            htmlFlex.style.flexWrap = 'nowrap'
            // Keep original direction unless it's column on mobile
            if (computedStyle.flexDirection === 'column' && window.innerWidth < 768) {
              htmlFlex.style.flexDirection = 'row'
            }
          }
        })
        
        // Override all responsive width classes (sm:, md:, lg:)
        const allWidthElements = element.querySelectorAll('[class*="w-"], [class*="max-w"], [class*="min-w"]')
        const widthRestore: Array<{
          element: HTMLElement
          originalWidth: string
          originalMaxWidth: string
          originalMinWidth: string
        }> = []
        
        allWidthElements.forEach((el) => {
          const htmlEl = el as HTMLElement
          const computedStyle = window.getComputedStyle(htmlEl)
          
          // Only override if it's a width constraint that might be responsive
          if (computedStyle.width.includes('%') || computedStyle.maxWidth.includes('px') && parseFloat(computedStyle.maxWidth) < 1200) {
            widthRestore.push({
              element: htmlEl,
              originalWidth: htmlEl.style.width,
              originalMaxWidth: htmlEl.style.maxWidth,
              originalMinWidth: htmlEl.style.minWidth
            })
            
            // Force desktop widths
            if (htmlEl.classList.contains('w-full') || htmlEl.classList.contains('w-screen')) {
              htmlEl.style.width = '100%'
              htmlEl.style.maxWidth = '100%'
            }
          }
        })
        
        // Temporarily adjust element for capture
        element.style.overflow = 'visible'
        element.style.height = 'auto'
        element.style.boxSizing = 'border-box'
        element.style.position = 'relative'
        
        // Optimize element for PDF: Reduce padding and gaps for more compact layout
        const allElements = element.querySelectorAll('*')
        const elementStyles: Array<{
          element: HTMLElement
          originalPadding: string
          originalMargin: string
          originalGap: string
          originalFontSize: string
        }> = []
        
        allElements.forEach((el) => {
          const htmlEl = el as HTMLElement
          const computedStyle = window.getComputedStyle(htmlEl)
          
          // Store original styles
          elementStyles.push({
            element: htmlEl,
            originalPadding: htmlEl.style.padding,
            originalMargin: htmlEl.style.margin,
            originalGap: htmlEl.style.gap,
            originalFontSize: htmlEl.style.fontSize
          })
          
          // Reduce padding for more compact layout
          if (computedStyle.padding && parseFloat(computedStyle.padding) > 8) {
            htmlEl.style.padding = '8px'
          }
          
          // Reduce margin for more compact layout
          if (computedStyle.margin && parseFloat(computedStyle.margin) > 8) {
            htmlEl.style.margin = '4px 0'
          }
          
          // Reduce font size slightly for more compact layout
          if (computedStyle.fontSize && parseFloat(computedStyle.fontSize) > 12) {
            const fontSize = parseFloat(computedStyle.fontSize)
            htmlEl.style.fontSize = `${Math.max(fontSize * 0.9, 11)}px`
          }
          
          // Force flex-wrap for responsive elements
          if (computedStyle.display === 'flex' && computedStyle.flexWrap === 'wrap') {
            htmlEl.style.flexWrap = 'nowrap'
          }
        })
        
        // Reduce element padding
        element.style.padding = '12px'
        
        // Wait for layout to settle and DOM to update
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Force multiple reflows to ensure all styles are applied
        element.offsetHeight
        element.scrollHeight
        await new Promise(resolve => requestAnimationFrame(resolve))
        await new Promise(resolve => requestAnimationFrame(resolve))
        
        // Calculate optimal scale to fit on single page
        // A4 page: 210mm x 297mm (portrait)
        // With margins: ~190mm x ~260mm available
        // Target: Image should fit in ~260mm height
        
        // Force desktop viewport for html2canvas (critical for mobile devices)
        const canvas = await html2canvas(element, {
          backgroundColor: '#ffffff',
          scale: 1.0, // Optimized scale for single page fit
          logging: false,
          useCORS: true,
          allowTaint: false,
          windowWidth: 1400, // Force desktop viewport width (larger for better rendering)
          windowHeight: 2000, // Force desktop viewport height
          width: 1200, // Fixed element width
          height: element.scrollHeight || element.offsetHeight,
          scrollX: 0, // No horizontal scroll
          scrollY: 0, // No vertical scroll
          x: 0,
          y: 0,
          ignoreElements: (el) => {
            // Ignore elements that are not needed in PDF
            if (el instanceof HTMLElement) {
              return el.classList.contains('hidden') || el.style.display === 'none'
            }
            return false
          },
          onclone: (clonedDoc, clonedElement) => {
            // Force desktop viewport on cloned document body and html
            const clonedBody = clonedDoc.body
            const clonedHtml = clonedDoc.documentElement
            
            clonedBody.style.width = '1400px'
            clonedBody.style.maxWidth = '1400px'
            clonedBody.style.overflow = 'visible'
            clonedHtml.style.width = '1400px'
            clonedHtml.style.maxWidth = '1400px'
            
            // Ensure cloned element has desktop layout
            if (clonedElement) {
              clonedElement.style.width = '1200px'
              clonedElement.style.maxWidth = '1200px'
              clonedElement.style.minWidth = '1200px'
              clonedElement.style.margin = '0 auto'
              clonedElement.style.padding = '16px'
              clonedElement.style.boxSizing = 'border-box'
              clonedElement.style.position = 'relative'
              clonedElement.style.overflow = 'visible'
            }
            
            // Force all grids to 4 columns in cloned document (override all responsive classes)
            const clonedGrids = clonedDoc.querySelectorAll('[class*="grid"], [class*="grid-cols"]')
            clonedGrids.forEach((grid) => {
              const htmlGrid = grid as HTMLElement
              const computedStyle = window.getComputedStyle(htmlGrid)
              
              if (computedStyle.display === 'grid' || htmlGrid.className.includes('grid')) {
                htmlGrid.style.display = 'grid'
                htmlGrid.style.gridTemplateColumns = 'repeat(4, minmax(0, 1fr))'
                htmlGrid.style.width = '100%'
                htmlGrid.style.maxWidth = '100%'
                htmlGrid.style.minWidth = '0'
                htmlGrid.style.gap = '10px'
                htmlGrid.style.flexBasis = 'auto'
                htmlGrid.style.flexShrink = '1'
                htmlGrid.style.flexGrow = '0'
              }
            })
            
            // Force all flex containers to not wrap (desktop behavior)
            const clonedFlexContainers = clonedDoc.querySelectorAll('[class*="flex"]')
            clonedFlexContainers.forEach((flex) => {
              const htmlFlex = flex as HTMLElement
              const computedStyle = window.getComputedStyle(htmlFlex)
              
              if (computedStyle.display === 'flex') {
                htmlFlex.style.flexWrap = 'nowrap'
                // Keep row direction for desktop
                if (computedStyle.flexDirection === 'column') {
                  htmlFlex.style.flexDirection = 'row'
                }
              }
            })
            
            // Override responsive width classes in cloned document
            const clonedWidthElements = clonedDoc.querySelectorAll('[class*="w-"], [class*="max-w"], [class*="min-w"]')
            clonedWidthElements.forEach((el) => {
              const htmlEl = el as HTMLElement
              
              // Force full width for containers
              if (htmlEl.classList.contains('w-full') || htmlEl.classList.contains('w-screen')) {
                htmlEl.style.width = '100%'
                htmlEl.style.maxWidth = '100%'
              }
            })
            
            // Reduce padding and margins in cloned document for compact layout
            const clonedAllElements = clonedDoc.querySelectorAll('*')
            clonedAllElements.forEach((el) => {
              const htmlEl = el as HTMLElement
              const computedStyle = window.getComputedStyle(htmlEl)
              
              // Reduce padding
              if (computedStyle.padding && parseFloat(computedStyle.padding) > 8) {
                htmlEl.style.padding = '8px'
              }
              
              // Reduce margin
              if (computedStyle.margin && parseFloat(computedStyle.margin) > 8) {
                htmlEl.style.margin = '4px 0'
              }
              
              // Reduce font size slightly for compact layout
              if (computedStyle.fontSize && parseFloat(computedStyle.fontSize) > 12) {
                const fontSize = parseFloat(computedStyle.fontSize)
                htmlEl.style.fontSize = `${Math.max(fontSize * 0.9, 11)}px`
              }
            })
          }
        })
        
        // Restore all element styles
        elementStyles.forEach(({ element: htmlEl, originalPadding, originalMargin, originalGap, originalFontSize }) => {
          htmlEl.style.padding = originalPadding
          htmlEl.style.margin = originalMargin
          htmlEl.style.gap = originalGap
          htmlEl.style.fontSize = originalFontSize
        })
        
        // Restore original element styles
        element.style.width = originalElementWidth
        element.style.maxWidth = originalElementMaxWidth
        element.style.padding = originalElementPadding
        element.style.margin = originalElementMargin
        element.style.boxSizing = ''
        
        // Restore grid containers
        gridRestore.forEach(({ 
          element: htmlGrid, 
          originalGap, 
          originalClass, 
          originalDisplay,
          originalGridTemplateColumns,
          originalWidth,
          originalMaxWidth,
          originalMinWidth
        }) => {
          htmlGrid.style.gap = originalGap
          htmlGrid.className = originalClass
          htmlGrid.style.display = originalDisplay
          htmlGrid.style.gridTemplateColumns = originalGridTemplateColumns
          htmlGrid.style.width = originalWidth
          htmlGrid.style.maxWidth = originalMaxWidth
          htmlGrid.style.minWidth = originalMinWidth
          htmlGrid.style.flexBasis = ''
          htmlGrid.style.flexShrink = ''
          htmlGrid.style.flexGrow = ''
        })
        
        // Restore flex containers
        flexRestore.forEach(({ element: htmlFlex, originalFlexWrap, originalFlexDirection }) => {
          htmlFlex.style.flexWrap = originalFlexWrap
          htmlFlex.style.flexDirection = originalFlexDirection
        })
        
        // Restore width elements
        widthRestore.forEach(({ element: htmlEl, originalWidth, originalMaxWidth, originalMinWidth }) => {
          htmlEl.style.width = originalWidth
          htmlEl.style.maxWidth = originalMaxWidth
          htmlEl.style.minWidth = originalMinWidth
        })
        
        // Restore body and html width styles (scroll will be restored in finally block)
        // Note: We don't restore overflow/position here as they're needed until PDF is complete
        document.body.style.width = originalBodyWidth
        document.body.style.maxWidth = originalBodyMaxWidth
        document.documentElement.style.width = originalHtmlWidth
        document.documentElement.style.maxWidth = originalHtmlMaxWidth
        
        // Restore original element styles
        element.style.minWidth = originalElementMinWidth
        element.style.overflow = originalOverflow
        element.style.height = originalHeight
        element.style.position = ''
        
        // Restore input/select elements (replace divs back with original inputs)
        inputReplacements.forEach(({ original, replacement, parent }) => {
          if (parent && replacement.parentNode === parent) {
            // Remove replacement div
            parent.removeChild(replacement)
            // Restore original input
            original.style.display = ''
          }
        })
        
        // Restore output div styles
        originalOutputStyles.forEach(({ element, padding, fontSize, height, overflow, textOverflow, whiteSpace, lineHeight }) => {
          element.style.padding = padding
          element.style.fontSize = fontSize
          element.style.height = height
          element.style.overflow = overflow
          element.style.textOverflow = textOverflow
          element.style.whiteSpace = whiteSpace
          element.style.lineHeight = lineHeight
        })
        
        // Grid gaps already restored above

        const imgData = canvas.toDataURL('image/png', 1.0)

        // Create PDF
        const doc = new jsPDF('p', 'mm', 'a4')
        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()
        
        let logoAdded = false
        
        // Add logo if available
        try {
          const logoResponse = await fetch('/images/logo_siyah.webp')
          if (logoResponse.ok) {
            const logoBlob = await logoResponse.blob()
            const logoDataUrl = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader()
              reader.onerror = reject
              reader.onloadend = () => {
                if (reader.result) {
                  resolve(reader.result as string)
                } else {
                  reject(new Error('Logo yüklenemedi'))
                }
              }
              reader.readAsDataURL(logoBlob)
            })
            
            // Convert WebP to PNG
            const img = new Image()
            img.src = logoDataUrl
            await new Promise<void>((resolve, reject) => {
              img.onload = () => {
                try {
                  const logoCanvas = document.createElement('canvas')
                  logoCanvas.width = img.width
                  logoCanvas.height = img.height
                  const ctx = logoCanvas.getContext('2d')
                  if (ctx) {
                    ctx.drawImage(img, 0, 0)
                    const pngDataUrl = logoCanvas.toDataURL('image/png', 0.95)
                    doc.addImage(pngDataUrl, 'PNG', 15, 10, 50, 15)
                    logoAdded = true
                    resolve()
                  } else {
                    reject(new Error('Canvas context alınamadı'))
                  }
                } catch (error) {
                  reject(error)
                }
              }
              img.onerror = reject
            })
          }
        } catch (logoError) {
          console.warn('Logo yüklenemedi:', logoError)
        }

        // Add title
        doc.setFontSize(18)
        doc.setFont('helvetica', 'bold')
        doc.text('E-Ticaret Ekonomisi Hesaplama Raporu', pageWidth / 2, logoAdded ? 35 : 20, { align: 'center' })

        // Add date with proper Turkish characters in top-right corner using canvas
        const now = new Date()
        const day = now.getDate()
        const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                       'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
        const month = months[now.getMonth()]
        const year = now.getFullYear()
        const currentDate = `${day} ${month} ${year}`
        const dateText = `Tarih: ${currentDate}`
        
        // Create a canvas to render Turkish characters correctly
        const dateCanvas = document.createElement('canvas')
        dateCanvas.width = 150
        dateCanvas.height = 20
        const dateCtx = dateCanvas.getContext('2d')
        
        if (dateCtx) {
          dateCtx.fillStyle = '#000000'
          dateCtx.font = '10px Arial, sans-serif'
          dateCtx.textAlign = 'right'
          dateCtx.textBaseline = 'middle'
          dateCtx.fillText(dateText, dateCanvas.width - 5, dateCanvas.height / 2)
          
          // Convert canvas to image and add to PDF in top-right corner
          const dateImgData = dateCanvas.toDataURL('image/png', 1.0)
          const dateWidth = 45 // mm
          const dateHeight = 5 // mm
          const dateX = pageWidth - dateWidth - 15 // 15mm margin from right
          const dateY = logoAdded ? 10 : 10 // Same height as logo
          doc.addImage(dateImgData, 'PNG', dateX, dateY, dateWidth, dateHeight)
        } else {
          // Fallback: Use doc.text if canvas is not available
          doc.setFontSize(10)
          doc.setFont('helvetica', 'normal')
          doc.text(dateText, pageWidth - 15, logoAdded ? 12 : 12, { align: 'right' })
        }

        // Add calculator image to PDF - Force single page layout
        let yPosition = logoAdded ? 45 : 30
        const margin = 8 // Reduced margin for more space
        const availableWidth = pageWidth - (margin * 2)
        const availableHeight = pageHeight - yPosition - 15 // Leave space for footer (reduced)

        // Calculate dimensions to fit the image on single page (maintain aspect ratio)
        const imgAspectRatio = canvas.width / canvas.height
        let imgWidth = availableWidth
        let imgHeight = imgWidth / imgAspectRatio

        // If image is too tall, scale it down to fit on single page (no multi-page splitting)
        if (imgHeight > availableHeight) {
          // Scale down to fit on single page
          const scaleFactor = availableHeight / imgHeight
          imgWidth = imgWidth * scaleFactor
          imgHeight = availableHeight
        }

        // Always add image to single page (no multi-page splitting)
        doc.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight)

        // Footer
        const totalPages = (doc as any).internal.getNumberOfPages()
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i)
          doc.setFontSize(8)
          doc.setTextColor(150, 150, 150)
          doc.text(
            `Sayfa ${i} / ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          )
          doc.text(
            'thinklyze.com',
            pageWidth - 20,
            pageHeight - 10,
            { align: 'right' }
          )
        }

        // Detect mobile devices
        const isMobile = window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        
        // Return PDF as Blob for sharing, or download it
        if (shareMode) {
          // Return PDF as Blob for sharing
          const pdfBlob = doc.output('blob')
          
          // On mobile devices, wait 1 second before returning to ensure overlay covers any layout shifts
          if (isMobile) {
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
          
          return pdfBlob
        } else {
          // Download PDF
          doc.save(`e-ticaret-ekonomisi-raporu-${new Date().getTime()}.pdf`)
          
          // On mobile devices, wait 1 second after download to ensure overlay covers any layout shifts
          if (isMobile) {
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        }
      } catch (captureError: any) {
        console.error('Görsel yakalanırken hata:', captureError)
        throw captureError
      }
    } catch (error: any) {
      console.error('PDF oluşturulurken hata:', error)
      console.error('Hata detayları:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      })
      alert(`PDF oluşturulurken bir hata oluştu: ${error?.message || 'Bilinmeyen hata'}. Lütfen konsolu kontrol edin ve tekrar deneyin.`)
    } finally {
      // Remove scroll prevention event listeners FIRST
      window.removeEventListener('scroll', preventScroll, { capture: true } as any)
      window.removeEventListener('wheel', preventScroll, { capture: true } as any)
      window.removeEventListener('touchmove', preventScroll, { capture: true } as any)
      window.removeEventListener('touchstart', preventScroll, { capture: true } as any)
      
      // Restore body styles BEFORE removing overlay to prevent flash
      document.body.style.overflow = originalBodyOverflow
      document.body.style.position = originalBodyPosition
      document.body.style.width = originalBodyWidth
      document.body.style.height = originalBodyHeight
      document.body.style.top = originalBodyTop
      document.body.style.left = originalBodyLeft
      document.body.style.right = originalBodyRight
      document.body.style.touchAction = originalBodyTouchAction
      document.body.style.overscrollBehavior = ''
      document.body.style.maxWidth = originalBodyMaxWidth
      
      // Restore html styles
      document.documentElement.style.overflow = originalHtmlOverflow
      document.documentElement.style.position = originalHtmlPosition
      document.documentElement.style.touchAction = originalHtmlTouchAction
      document.documentElement.style.overscrollBehavior = ''
      document.documentElement.style.width = originalHtmlWidth
      document.documentElement.style.maxWidth = originalHtmlMaxWidth
      
      // Force reflow after restoring styles
      document.body.offsetHeight
      
      // Remove loading overlay with fade-out animation
      // Overlay is removed after styles are restored so it covers any potential layout shift
      if (loadingOverlay && document.body.contains(loadingOverlay)) {
        // Add fade-out animation
        loadingOverlay.style.animation = 'fadeOut 0.2s ease-out'
        loadingOverlay.style.opacity = '0'
        
        // Remove after animation completes
        setTimeout(() => {
          if (loadingOverlay && document.body.contains(loadingOverlay)) {
            document.body.removeChild(loadingOverlay)
          }
          
          // Restore scroll position AFTER overlay is completely removed
          // Use requestAnimationFrame to ensure DOM is ready and prevent layout shift
          requestAnimationFrame(() => {
            // Restore scroll position without animation
            window.scrollTo({
              top: scrollPosition,
              left: 0,
              behavior: 'auto' // Use 'auto' instead of 'smooth' to prevent animation
            })
            document.documentElement.scrollTop = scrollPosition
          })
        }, 200)
      } else {
        // If overlay was already removed, restore scroll immediately
        requestAnimationFrame(() => {
          window.scrollTo({
            top: scrollPosition,
            left: 0,
            behavior: 'auto'
          })
          document.documentElement.scrollTop = scrollPosition
        })
      }
      
      // Remove style element if it exists
      if (styleElement && document.head.contains(styleElement)) {
        document.head.removeChild(styleElement)
      }
    }
  }

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{pageContent.ecommerceCalculator.title}</h2>
            <p className="text-sm sm:text-base text-gray-600">{pageContent.ecommerceCalculator.description}</p>
          </div>
          <button
            onClick={() => setIsVideoModalOpen(true)}
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
          >
            <Play className="w-4 h-4" />
            <span>{pageContent.ecommerceCalculator.videoTitle}</span>
          </button>
        </div>
      </div>

      {/* Calculator Content - This will be captured for PDF */}
      <div ref={calculatorRef} data-calculator-ref="true" className="bg-white rounded-lg p-4 sm:p-6">
        {/* Warning Box */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <p className="text-xs sm:text-sm text-yellow-800">
            {pageContent.ecommerceCalculator.warning}
          </p>
        </div>

        {/* Sales and Cost Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.unitPrice || 'Ürün Satış Fiyatı (₺)'}
          </label>
          <input
            type="number"
            value={formatInputValue(rawProductPrice)}
            onChange={(e) => handleInputChange('productPrice', e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 text-sm border-2 border-green-300 bg-green-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.vatRate || 'KDV Oranı (%)'}
          </label>
          <select
            value={vatRate}
            onChange={(e) => setVatRate(parseFloat(e.target.value))}
            className="w-full px-3 py-2 text-sm border-2 border-pink-300 bg-pink-50 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 h-[42px] appearance-none cursor-pointer"
            style={{ minHeight: '42px' }}
          >
            <option value="0">0%</option>
            <option value="1">1%</option>
            <option value="10">10%</option>
            <option value="20">20%</option>
          </select>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.vatIncludedPrice || (language === 'tr' ? 'KDV Dahil Satış Fiyatı (₺)' : language === 'en' ? 'VAT Included Sales Price (₺)' : 'Цена с НДС (₺)')}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {vatIncludedPrice.toFixed(2)}
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.unitCost || 'Satılan Ürün Maliyeti - COGS (₺)'}
          </label>
          <input
            type="number"
            value={formatInputValue(rawCogs)}
            onChange={(e) => handleInputChange('cogs', e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 text-sm border-2 border-green-300 bg-green-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.wholesaleDepot || 'Toptan Alım + Depo Geliş (₺)'}
          </label>
          <input
            type="number"
            value={formatInputValue(rawWholesaleDepot)}
            onChange={(e) => handleInputChange('wholesaleDepot', e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 text-sm border-2 border-green-300 bg-green-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.marketplaceCommission || 'Komisyon Oranı (%)'}
          </label>
          <input
            type="number"
            value={formatInputValue(rawCommissionRatePercent)}
            onChange={(e) => handleInputChange('commissionRatePercent', e.target.value)}
            placeholder="0"
            step="0.01"
            className="w-full px-3 py-2 text-sm border-2 border-green-300 bg-green-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
          <div className="mt-1 text-xs text-gray-500">
            {pageContent?.ecommerceCalculator?.inputs?.marketplaceCommissionAmount || 'Komisyon Tutarı'}: {commissionRate.toFixed(2)} ₺
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.paymentInfraCommission || 'Ödeme Altyapı Komisyon Oranı (%)'}
          </label>
          <input
            type="number"
            value={formatInputValue(rawPaymentInfraCommissionRate)}
            onChange={(e) => handleInputChange('paymentInfraCommissionRate', e.target.value)}
            placeholder={pageContent?.ecommerceCalculator?.inputs?.paymentInfraCommissionPlaceholder || 'Paytr, İyzico vs. Örneğin 3.75'}
            step="0.01"
            className="w-full px-3 py-2 text-sm border-2 border-green-300 bg-green-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 placeholder:text-gray-400"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.paymentInfraCost || 'Ödeme Altyapı Maliyeti (₺)'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {paymentInfraCost.toFixed(2)}
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.packagingCost || 'Paketleme Maliyeti (₺)'}
          </label>
          <input
            type="number"
            value={formatInputValue(rawPackagingCost)}
            onChange={(e) => handleInputChange('packagingCost', e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 text-sm border-2 border-green-300 bg-green-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.shippingCost || 'Kargo Maliyeti (₺)'}
          </label>
          <input
            type="number"
            value={formatInputValue(rawShippingCost)}
            onChange={(e) => handleInputChange('shippingCost', e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 text-sm border-2 border-green-300 bg-green-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.returnRate || 'İade Oranı (%)'}
          </label>
          <input
            type="number"
            value={formatInputValue(rawReturnRate)}
            onChange={(e) => handleInputChange('returnRate', e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 text-sm border-2 border-green-300 bg-green-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.returnRateCost || 'İade Oranı Maliyeti (₺)'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {returnCost.toFixed(2)}
          </div>
        </div>
      </div>

      {/* COD, Profit and Others */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.codExpenses || 'Toplam COD Giderleri (₺)'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {codExpenses.toFixed(2)}
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.outputs?.grossProfit || 'Brüt Kâr (₺)'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {grossProfit.toFixed(2)}
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.outputs?.cac || 'Müşteri Edinim Maliyeti (CAC)'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {cacTarget.toFixed(2)}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {pageContent?.ecommerceCalculator?.outputs?.cacFormula || (language === 'tr' ? 'Brüt Kar - Net Kar' : language === 'en' ? 'Gross Profit - Net Profit' : 'Валовая прибыль - Чистая прибыль')}
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.inputs?.netProfitTarget || 'Net Kâr Hedefi (₺)'}
          </label>
          <input
            type="number"
            value={formatInputValue(rawNetProfitTarget)}
            onChange={(e) => handleInputChange('netProfitTarget', e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 text-sm border-2 border-green-300 bg-green-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.outputs?.roas || 'ROAS'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {roas > 0 ? roas.toFixed(2) : '0.00'}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {pageContent?.ecommerceCalculator?.outputs?.revenueCacDescription?.replace('{revenue}', totalRevenue.toFixed(2))?.replace('{cac}', cacTarget.toFixed(2)) || (language === 'tr' ? `Gelir: ${totalRevenue.toFixed(2)} ₺ / CAC: ${cacTarget.toFixed(2)} ₺` : language === 'en' ? `Revenue: ${totalRevenue.toFixed(2)} ₺ / CAC: ${cacTarget.toFixed(2)} ₺` : `Доход: ${totalRevenue.toFixed(2)} ₺ / CAC: ${cacTarget.toFixed(2)} ₺`)}
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.outputs?.roi || 'ROI (%)'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {roi > 0 ? roi.toFixed(2) : '0.00'}%
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {pageContent?.ecommerceCalculator?.outputs?.roiFormula || (roi > 0 ? (language === 'tr' ? 'Kârlı' : language === 'en' ? 'Profitable' : 'Прибыльно') : roi < 0 ? (language === 'tr' ? 'Zararlı' : language === 'en' ? 'Loss' : 'Убыток') : 'Break-even')}
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.outputs?.breakEvenRoas || 'Break-even ROAS'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {breakEvenRoas > 0 ? breakEvenRoas.toFixed(2) : '0.00'}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {breakEvenRoas > 0 
              ? (pageContent?.ecommerceCalculator?.outputs?.breakEvenDescription 
                  ? (language === 'tr' 
                      ? `${breakEvenRoas.toFixed(2)} ${pageContent.ecommerceCalculator.outputs.breakEvenDescription}`
                      : language === 'en'
                      ? `Below ${breakEvenRoas.toFixed(2)} ${pageContent.ecommerceCalculator.outputs.breakEvenDescription}`
                      : `Ниже ${breakEvenRoas.toFixed(2)} ${pageContent.ecommerceCalculator.outputs.breakEvenDescription}`)
                  : (language === 'tr' ? `${breakEvenRoas.toFixed(2)} altında zarar, üstünde kâr` : language === 'en' ? `Below ${breakEvenRoas.toFixed(2)} loss, above profit` : `Ниже ${breakEvenRoas.toFixed(2)} убыток, выше прибыль`))
              : (pageContent?.ecommerceCalculator?.outputs?.breakEvenFormula || (language === 'tr' ? 'Hesaplanamıyor' : language === 'en' ? 'Cannot calculate' : 'Не может быть рассчитано'))}
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.outputs?.avgOrderCount || 'Müşteri Ortalama Sipariş Sayısı'}
          </label>
          <input
            type="number"
            value={formatInputValue(rawAvgOrderCount)}
            onChange={(e) => handleInputChange('avgOrderCount', e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 text-sm border-2 border-green-300 bg-green-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
      </div>

      {/* Performance Metrics and Others */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.outputs?.avgBasket || 'Sepet Ortalaması (₺)'}
          </label>
          <input
            type="number"
            value={formatInputValue(rawAvgBasket)}
            onChange={(e) => handleInputChange('avgBasket', e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 text-sm border-2 border-green-300 bg-green-50 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.outputs?.ltv || 'LTV (₺)'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {ltv.toFixed(2)}
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.outputs?.ltvCacRatio || 'LTV / CAC Oranı'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {ltvCacRatio > 0 ? ltvCacRatio.toFixed(2) : '0.00'}
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.outputs?.netProfitPercent || 'Net Kâr % (%)'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {netProfitPercent.toFixed(2)}%
          </div>
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            {pageContent?.ecommerceCalculator?.outputs?.grossProfitPercent || 'Brüt Kâr % (%)'}
          </label>
          <div className="w-full px-3 py-2 text-sm border-2 border-yellow-300 bg-yellow-50 rounded-lg text-gray-700 font-semibold">
            {grossProfitPercent.toFixed(2)}%
          </div>
        </div>
      </div>

        {/* VAT Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-xs sm:text-sm text-blue-800">
            <strong>{pageContent?.ecommerceCalculator?.vatNote?.title || 'KDV Notu:'}</strong> {pageContent?.ecommerceCalculator?.vatNote?.text || 'KDV, devlet adına tahsil edilen bir vergi olduğu için: • İşletmenin kazancı değildir. • Kârlılık analizlerine, maliyet ve ROAS hesaplarına dahil edilmez.'}
          </p>
        </div>
      </div>

      {/* Action Buttons - Outside of ref so they're not in PDF */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => generatePDF(false)}
          className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          <Download className="w-4 h-4" />
          {pageContent?.buttons?.downloadPDF || 'PDF Rapor İndir'}
        </button>
        <button
          onClick={async () => {
            try {
              // Generate PDF for sharing
              const pdfBlob = await generatePDF(true) as Blob
              
              if (!pdfBlob) {
                throw new Error('PDF oluşturulamadı')
              }
              
              // Create a File object from the Blob
              const fileName = `e-ticaret-ekonomisi-raporu-${new Date().getTime()}.pdf`
              const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' })
              
              // Share PDF if Web Share API is available
              if (navigator.share && navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
                await navigator.share({
                  title: 'E-Ticaret Ekonomisi Hesaplama Raporu',
                  text: `E-Ticaret Ekonomisi Analizi:\nROAS: ${roas.toFixed(2)}\nROI: ${roi.toFixed(2)}%\nLTV/CAC: ${ltvCacRatio.toFixed(2)}`,
                  files: [pdfFile]
                })
              } else if (navigator.share) {
                // Fallback: Share text if file sharing is not supported
                const shareText = `E-Ticaret Ekonomisi Analizi:\nROAS: ${roas.toFixed(2)}\nROI: ${roi.toFixed(2)}%\nLTV/CAC: ${ltvCacRatio.toFixed(2)}\n\nPDF raporu indirmek için "PDF Rapor İndir" butonunu kullanın.`
                await navigator.share({ text: shareText })
              } else {
                // Fallback: Download PDF and copy text to clipboard
                const url = URL.createObjectURL(pdfBlob)
                const a = document.createElement('a')
                a.href = url
                a.download = fileName
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(url)
                
                // Also copy text to clipboard
                const shareText = `E-Ticaret Ekonomisi Analizi:\nROAS: ${roas.toFixed(2)}\nROI: ${roi.toFixed(2)}%\nLTV/CAC: ${ltvCacRatio.toFixed(2)}`
                await navigator.clipboard.writeText(shareText)
                alert('PDF indirildi ve sonuç panoya kopyalandı!')
              }
            } catch (error: any) {
              console.error('Paylaşma hatası:', error)
              // If sharing fails, try to download the PDF
              try {
                await generatePDF(false)
                alert('Paylaşma başarısız oldu. PDF indirildi.')
              } catch (downloadError) {
                alert('PDF oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.')
              }
            }
          }}
          className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
        >
          <Share className="w-4 h-4" />
          {pageContent?.buttons?.shareResult || 'Sonucu Paylaş'}
        </button>
      </div>

      {/* FAQ Section */}
      <FAQAccordion
        title={pageContent?.faq?.title || 'Sık Sorulan Sorular'}
        subtitle={pageContent?.faq?.subtitle || 'E-Ticaret Ekonomisi Hesaplama Aracı hakkında merak ettiğiniz soruların yanıtları'}
        faqs={faqs}
        showCTA={false}
      />

      {/* YouTube Video Modal */}
      {isVideoModalOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setIsVideoModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Nasıl Kullanılır?</h3>
              <button
                onClick={() => setIsVideoModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Kapat"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* YouTube Embed */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-b-xl"
                src="https://www.youtube.com/embed/oTQZNodzc5Q?start=1"
                title="E-Ticaret Ekonomisi Hesaplama Aracı Nasıl Kullanılır?"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarketingTools













